import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Moon, LogOut, Send, Loader2, CheckCircle, Clock, Trophy } from 'lucide-react';
import { api } from '@/lib/api';
import { useAuth } from '@/hooks/useAuth';
import { getArabicOrdinal } from '@/lib/utils';
import type { TodayQuestionResponse } from '@/types';

export default function Competition() {
  const { user, logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [questionData, setQuestionData] = useState<TodayQuestionResponse | null>(null);
  const [answer, setAnswer] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadTodayQuestion();
  }, []);

  const loadTodayQuestion = async () => {
    try {
      const response = await api.getTodayQuestion();
      if (response.success) {
        setQuestionData(response.data || null);
      }
    } catch (err) {
      console.error('Failed to load question:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!questionData?.question || !answer.trim()) return;

    setSubmitting(true);
    setError('');

    try {
      const response = await api.submitAnswer({
        question_id: questionData.question.id,
        answer_text: answer.trim(),
      });

      if (response.success) {
        setSubmitted(true);
      } else {
        setError(response.error || 'حدث خطأ أثناء إرسال الإجابة');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'حدث خطأ أثناء إرسال الإجابة');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-gold-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Header */}
      <header className="border-b border-gray-800 px-4 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Moon className="w-8 h-8 text-gold-500" />
            <span className="text-xl font-amiri font-bold text-gold-500">رحلة عبر الزمن</span>
          </Link>

          <div className="flex items-center gap-4">
            <div className="text-left">
              <p className="text-cream font-medium">{user?.full_name}</p>
              <p className="text-sm text-gold-500 flex items-center gap-1">
                <Trophy className="w-4 h-4" />
                {user?.total_score} نقطة
              </p>
            </div>
            <button
              onClick={logout}
              className="p-2 text-gray-400 hover:text-red-400 transition-colors"
              title="تسجيل الخروج"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Progress Bar */}
        <div className="card mb-8">
          <h3 className="text-lg font-bold text-cream mb-4">تقدمك في المسابقة</h3>
          <div className="flex items-center gap-2">
            {[1, 2, 3, 4, 5].map((station) => (
              <div
                key={station}
                className={`flex-1 h-3 rounded-full ${
                  (questionData?.question?.current_station || 1) >= station
                    ? 'bg-gold-500'
                    : 'bg-gray-700'
                }`}
              />
            ))}
          </div>
          <p className="text-sm text-gray-400 mt-2">
            المحطة الحالية: {questionData?.question?.current_station || 1} من 5
          </p>
        </div>

        {/* Question Section */}
        {!questionData?.question ? (
          <div className="card text-center py-12">
            <Clock className="w-16 h-16 text-gold-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-cream mb-2">
              {questionData?.message || 'المسابقة لم تبدأ بعد'}
            </h2>
            <p className="text-gray-400">
              ترقب سؤال اليوم قريباً!
            </p>
          </div>
        ) : questionData.already_answered || submitted ? (
          <div className="card text-center py-12">
            <CheckCircle className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-cream mb-2">
              تم إرسال إجابتك!
            </h2>
            <p className="text-gray-400">
              سيتم تقييم إجابتك وإضافة النقاط قريباً
            </p>
            <Link to="/leaderboard" className="btn-secondary inline-block mt-6">
              عرض لوحة المتصدرين
            </Link>
          </div>
        ) : (
          <div className="card">
            {/* Day Badge */}
            <div className="inline-block bg-gold-500/10 border border-gold-500/30 rounded-full px-4 py-1 mb-4">
              <span className="text-gold-500 font-bold">
                اليوم {getArabicOrdinal(questionData.day)}
              </span>
            </div>

            {/* Question Image */}
            {questionData.question.image_url && (
              <div className="mb-6 rounded-lg overflow-hidden border border-gray-700">
                <img
                  src={questionData.question.image_url}
                  alt="صورة السؤال"
                  className="w-full h-auto"
                />
              </div>
            )}

            {/* Question Text */}
            <h2 className="text-2xl font-amiri font-bold text-cream mb-6">
              {questionData.question.question_text}
            </h2>

            {/* Hint */}
            {questionData.question.hint && (
              <p className="text-gray-400 text-sm mb-6 bg-gray-800/50 rounded-lg p-4">
                💡 تلميح: {questionData.question.hint}
              </p>
            )}

            {/* Answer Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-4 text-red-400">
                  {error}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  إجابتك
                </label>
                <textarea
                  required
                  rows={4}
                  className="input-field resize-none"
                  placeholder="اكتب إجابتك هنا..."
                  value={answer}
                  onChange={(e) => setAnswer(e.target.value)}
                />
              </div>

              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-400">
                  النقاط: <span className="text-gold-500 font-bold">{questionData.question.points}</span>
                </p>
                <button
                  type="submit"
                  disabled={submitting || !answer.trim()}
                  className="btn-primary flex items-center gap-2"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      جاري الإرسال...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      إرسال الإجابة
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        )}
      </main>
    </div>
  );
}
